import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import { CurrencyProvider } from './components/CurrencyProvider';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import TrainerDashboard from './pages/TrainerDashboard';
import CorporateDashboard from './pages/CorporateDashboard';
import SearchResults from './pages/SearchResults';
import TrainerProfile from './pages/TrainerProfile';
import JobDetail from './pages/JobDetail';
import PostJob from './pages/PostJob';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboard from './pages/AdminDashboard';
import TermsPage from './pages/TermsPage';
import PricingPage from './pages/PricingPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import FeedbackPage from './pages/FeedbackPage';
import CorporateJobsDetail from './pages/CorporateJobsDetail';
import CorporateBookingsDetail from './pages/CorporateBookingsDetail';
import CorporateSpendingDetail from './pages/CorporateSpendingDetail';
import TrainerBookingsDetail from './pages/TrainerBookingsDetail';
import TrainerEarningsDetail from './pages/TrainerEarningsDetail';
import TrainerRatingsDetail from './pages/TrainerRatingsDetail';
import TrainerApplicationsDetail from './pages/TrainerApplicationsDetail';
import Messages from './pages/Messages';
import Shortlist from './pages/Shortlist';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(sessionStorage.getItem('token'));

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      sessionStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    sessionStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    sessionStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    setToken(null);
    setUser(null);
    toast.success('Logged out successfully');
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedTypes }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    // Redirect to appropriate login page
    if (allowedTypes?.includes('admin')) {
      return <Navigate to="/admin/login" state={{ from: location }} replace />;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedTypes && !allowedTypes.includes(user.user_type)) {
    // Redirect to appropriate dashboard
    if (user.user_type === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    }
    return <Navigate to={user.user_type === 'trainer' ? '/trainer/dashboard' : '/corporate/dashboard'} replace />;
  }

  return children;
};

function App() {
  return (
    <HelmetProvider>
    <CurrencyProvider>
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/feedback/:linkId" element={<FeedbackPage />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/trainer/:id" element={<TrainerProfile />} />
          <Route path="/job/:id" element={<JobDetail />} />
          
          {/* Trainer Routes */}
          <Route path="/trainer/dashboard" element={
            <ProtectedRoute allowedTypes={['trainer']}>
              <TrainerDashboard />
            </ProtectedRoute>
          } />
          
          {/* Corporate Routes */}
          <Route path="/corporate/dashboard" element={
            <ProtectedRoute allowedTypes={['corporate']}>
              <CorporateDashboard />
            </ProtectedRoute>
          } />
          <Route path="/corporate/post-job" element={
            <ProtectedRoute allowedTypes={['corporate']}>
              <PostJob />
            </ProtectedRoute>
          } />
          <Route path="/corporate/jobs" element={<ProtectedRoute allowedTypes={['corporate']}><CorporateJobsDetail /></ProtectedRoute>} />
          <Route path="/corporate/bookings" element={<ProtectedRoute allowedTypes={['corporate']}><CorporateBookingsDetail /></ProtectedRoute>} />
          <Route path="/corporate/spending" element={<ProtectedRoute allowedTypes={['corporate']}><CorporateSpendingDetail /></ProtectedRoute>} />
          <Route path="/corporate/shortlist" element={<ProtectedRoute allowedTypes={['corporate']}><Shortlist /></ProtectedRoute>} />

          {/* Trainer Detail Routes */}
          <Route path="/trainer/bookings" element={<ProtectedRoute allowedTypes={['trainer']}><TrainerBookingsDetail /></ProtectedRoute>} />
          <Route path="/trainer/earnings" element={<ProtectedRoute allowedTypes={['trainer']}><TrainerEarningsDetail /></ProtectedRoute>} />
          <Route path="/trainer/ratings" element={<ProtectedRoute allowedTypes={['trainer']}><TrainerRatingsDetail /></ProtectedRoute>} />
          <Route path="/trainer/applications" element={<ProtectedRoute allowedTypes={['trainer']}><TrainerApplicationsDetail /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute allowedTypes={['trainer', 'corporate']}><Messages /></ProtectedRoute>} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/dashboard" element={
            <ProtectedRoute allowedTypes={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </CurrencyProvider>
    </HelmetProvider>
  );
}

export default App;
